import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { MapPin, Calendar, Users, Search, Minus, Plus, ChevronDown } from "lucide-react";
import CityAutocomplete from "../CityAutocomplete/CityAutocomplete";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Label } from "../ui/label";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { getKeywordBasedSearch } from "../../api/services/hostService";
import hostelHero from '../../assets/hostel-hero.jpg';

const todayStr = () => new Date().toISOString().split('T')[0];
const tomorrowStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};

const HeroSection = () => {
  const [maleTravelers, setMaleTravelers] = useState(1);
  const [femaleTravelers, setFemaleTravelers] = useState(0);
  const [bookingType, setBookingType] = useState("bed");
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [destination, setDestination] = useState("");
  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState(tomorrowStr());
  const navigate = useNavigate();

  const totalTravelers = maleTravelers + femaleTravelers;

  const handleApply = () => {
    setIsOpen(false);
  };

  const handleSearch = () => {
    const queryParams = new URLSearchParams();
    if (destination) queryParams.append('keyword', destination);
    queryParams.append('checkin', checkIn || todayStr());
    queryParams.append('checkout', checkOut || tomorrowStr());
    queryParams.append('male_guests', maleTravelers.toString());
    queryParams.append('female_guests', femaleTravelers.toString());
    queryParams.append('accommodation_type', bookingType === 'bed' ? 'bed' : 'room');
    navigate(`/guest-search-results?${queryParams.toString()}`);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with overlay - optimized for mobile */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${hostelHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
        }}
      >
        <div className="absolute inset-0 bg-hero-overlay/60 md:bg-hero-overlay/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 pt-20">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6"
          >
            Bharat's affordable
            <br />
            <span className="text-secondary">stay platform</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto"
          >
            Your perfect stay, all inclusive. Bed, Breakfast, Lunch & Dinner starting from ₹499.
          </motion.p>
        </div>

        {/* Search Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-card rounded-2xl shadow-2xl p-6 md:p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Destination */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <MapPin size={16} />
                  Destination
                </label>
                <CityAutocomplete
                  value={destination}
                  onChange={setDestination}
                  placeholder="e.g. Bangalore, BTM"
                  inputClassName="w-full h-10 rounded-md border-0 bg-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              {/* Check-in */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Calendar size={16} />
                  Check-in
                </label>
                <Input
                  type="date"
                  className="border-0 bg-muted focus-visible:ring-primary"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                />
              </div>

              {/* Check-out */}
              <div className="space-y-2">
                <label className="text-sm font--medium text-muted-foreground flex items-center gap-2">
                  <Calendar size={16} />
                  Check-out
                </label>
                <Input
                  type="date"
                  className="border-0 bg-muted focus-visible:ring-primary"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                />
              </div>

              {/* Travelers Dropdown */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users size={16} />
                  Travelers
                </label>
                <Popover open={isOpen} onOpenChange={setIsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full justify-between border-0 bg-muted hover:bg-muted/80 text-left font-normal h-10"
                    >
                      <span className="text-muted-foreground">
                        {totalTravelers} Traveler{totalTravelers !== 1 ? 's' : ''}
                      </span>
                      <ChevronDown size={16} className="text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-4" align="start">
                    <div className="space-y-4">
                      {/* Male Travelers */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Male Travelers</span>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => setMaleTravelers(Math.max(0, maleTravelers - 1))}
                          >
                            <Minus size={14} />
                          </Button>
                          <span className="w-6 text-center font-medium">{maleTravelers}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => setMaleTravelers(maleTravelers + 1)}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>

                      {/* Female Travelers */}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Female Travelers</span>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => setFemaleTravelers(Math.max(0, femaleTravelers - 1))}
                          >
                            <Minus size={14} />
                          </Button>
                          <span className="w-6 text-center font-medium">{femaleTravelers}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-full"
                            onClick={() => setFemaleTravelers(femaleTravelers + 1)}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>

                      {/* Booking Type */}
                      <div className="space-y-2">
                        <span className="text-sm font-medium">Booking Type</span>
                        <RadioGroup value={bookingType} onValueChange={setBookingType} className="flex gap-4">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="bed" id="shared" />
                            <Label htmlFor="shared" className="text-sm cursor-pointer">Shared Bed</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="room" id="private" />
                            <Label htmlFor="private" className="text-sm cursor-pointer">Private Room</Label>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Apply Button */}
                      <Button
                        className="w-full bg-primary hover:bg-primary/90"
                        onClick={handleApply}
                      >
                        Apply
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-12 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                onClick={handleSearch}
              >
                <Search className="mr-2" size={20} />
                Search
              </Button>
            </div>
            {errorMessage && <p className="text-red-500 text-center mt-4">{errorMessage}</p>}
          </div>
        </motion.div>
      </div>

      {/* Smooth transition to USP */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-secondary to-transparent" />
    </section>
  );
};

export default HeroSection;
